import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/ui/PageHeader";
import { myTickets, type MyTicket } from "@/api/tickets";

/**
 * My tickets — the attendee's wallet. Shows the ticket code big enough to
 * read at a door scan (the Lab's stand-in for a QR render) and links back
 * to the event thread.
 */
export default function MyTicketsLab() {
  const { data, isLoading } = useQuery({ queryKey: ["my-tickets"], queryFn: myTickets });
  const upcoming = data?.data?.upcoming ?? [];
  const past = data?.data?.past ?? [];

  return (
    <div className="space-y-6">
      <PageHeader title="My Tickets" subtitle="Issued tickets — show the code at check-in" />
      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}

      <Section title={`Upcoming (${upcoming.length})`} tickets={upcoming} empty="No upcoming tickets — grab one from an event's checkout." />
      <Section title={`Past (${past.length})`} tickets={past} empty="No past tickets yet." />
    </div>
  );
}

function Section({ title, tickets, empty }: { title: string; tickets: MyTicket[]; empty: string }) {
  return (
    <div className="card p-5">
      <h2 className="font-semibold mb-3">{title}</h2>
      {tickets.length === 0 && <p className="text-sm text-gray-500">{empty}</p>}
      <div className="space-y-3">
        {tickets.map((t) => (
          <div key={t.id} className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <p className="font-medium">{t.event?.title ?? "Event"}</p>
                <p className="text-xs text-gray-500">
                  {t.event?.eventDate ? new Date(t.event.eventDate).toLocaleDateString() : ""}
                  {t.tier?.name ? ` · ${t.tier.name}` : ""}
                  {t.attendeeName ? ` · ${t.attendeeName}` : ""}
                </p>
              </div>
              <span
                className={`text-xs px-2 py-0.5 rounded-full ${
                  t.status === "checked_in"
                    ? "bg-twins-light text-twins-dark"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                {t.status}
              </span>
            </div>
            {/* The code IS the QR for Lab purposes — big and monospaced */}
            <p className="mt-3 font-mono text-lg tracking-wider bg-gray-50 rounded p-2 text-center select-all">
              {t.ticketCode}
            </p>
            {t.event?.id && (
              <Link to={`/lab/events/${t.event.id}/thread`} className="text-sm text-primary-600 hover:underline mt-2 inline-block">
                event thread →
              </Link>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
